import { useMemo } from 'react';
import { ChevronRight, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import {
  BOARD_STATUSES,
  STATUS_LABELS,
  type CreateTaskInput,
  type Task,
  type TaskPatch,
  type TaskStatus,
} from '../../api/organizer';
import { DueDateField } from './fields/DueDateField';
import { PriorityField } from './fields/PriorityField';
import { InlineTextEdit } from './fields/InlineTextEdit';
import { QuickAddTask } from './QuickAddTask';
import { Badge } from '../../components/ui/Badge';
import { isOverdue, sortForList } from './taskUtils';

interface TaskListProps {
  tasks: Task[];
  pendingIds: Set<string>;
  onPatch: (taskId: string, patch: TaskPatch) => void;
  onCreate: (input: CreateTaskInput) => void;
  onOpen: (taskId: string) => void;
  onDelete: (taskId: string) => void;
}

/**
 * A flat, dated list of everything open.
 *
 * Ordered by due date rather than by rank: the board answers "what state is this
 * in", the list answers "what is next", and those are different orders. Status
 * is a dropdown here instead of a drag, which also makes this view the keyboard-
 * and touch-complete way to work the board.
 */
export function TaskList({
  tasks,
  pendingIds,
  onPatch,
  onCreate,
  onOpen,
  onDelete,
}: TaskListProps) {
  const { open, done } = useMemo(() => {
    const openTasks = sortForList(tasks.filter((t) => t.status !== 'done'));
    const doneTasks = tasks
      .filter((t) => t.status === 'done')
      .sort((a, b) => (a.completedAt ?? '') < (b.completedAt ?? '') ? 1 : -1);
    return { open: openTasks, done: doneTasks };
  }, [tasks]);

  return (
    <div className="space-y-6">
      <QuickAddTask status="todo" onCreate={onCreate} />

      {open.length === 0 && done.length === 0 ? null : (
        <div className="divide-y divide-border-light rounded-xl border border-border-light">
          {open.map((task) => (
            <Row
              key={task.id}
              task={task}
              pending={pendingIds.has(task.id)}
              onPatch={onPatch}
              onOpen={onOpen}
              onDelete={onDelete}
            />
          ))}

          {done.length > 0 && (
            <details className="group">
              <summary className="cursor-pointer px-4 py-2.5 text-body-xs text-text-tertiary hover:text-text-primary">
                {done.length} completed
              </summary>
              <div className="divide-y divide-border-light border-t border-border-light">
                {done.map((task) => (
                  <Row
                    key={task.id}
                    task={task}
                    pending={pendingIds.has(task.id)}
                    onPatch={onPatch}
                    onOpen={onOpen}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

interface RowProps {
  task: Task;
  pending: boolean;
  onPatch: (taskId: string, patch: TaskPatch) => void;
  onOpen: (taskId: string) => void;
  onDelete: (taskId: string) => void;
}

function Row({ task, pending, onPatch, onOpen, onDelete }: RowProps) {
  const done = task.status === 'done';

  return (
    <div
      className={clsx(
        'group flex items-center gap-3 px-4 py-2.5',
        pending && 'opacity-60',
        isOverdue(task) && 'bg-error-light/40',
      )}
    >
      <input
        type="checkbox"
        checked={done}
        aria-label={done ? `Reopen ${task.title}` : `Complete ${task.title}`}
        onChange={() => onPatch(task.id, { status: done ? 'todo' : 'done' })}
        className="h-4 w-4 shrink-0 cursor-pointer rounded border-border accent-black"
      />

      <div className="min-w-0 flex-1">
        <InlineTextEdit
          value={task.title}
          muted={done}
          onCommit={(title) => onPatch(task.id, { title })}
          className="text-body-sm text-text-primary"
        />
      </div>

      {task.source === 'fathom' && (
        <span title={task.sourceMeta?.meetingTitle ?? 'From a call'} className="shrink-0">
          <Badge>From call</Badge>
        </span>
      )}

      <select
        value={task.status}
        aria-label={`Status of ${task.title}`}
        onChange={(e) => onPatch(task.id, { status: e.target.value as TaskStatus })}
        // "To do" is every open row's status, so it stays out of sight until hover
        // or focus; other statuses show, since they are the exception.
        className={clsx(
          'shrink-0 rounded-lg border border-border-light bg-surface-primary px-2 py-1 text-body-xs text-text-secondary outline-none transition-opacity focus:border-text-primary',
          task.status === 'todo' && 'opacity-0 group-hover:opacity-100 focus:opacity-100',
        )}
      >
        {BOARD_STATUSES.map((status) => (
          <option key={status} value={status}>
            {STATUS_LABELS[status]}
          </option>
        ))}
      </select>

      <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <button
          type="button"
          aria-label={`Open ${task.title}`}
          onClick={() => onOpen(task.id)}
          className="rounded p-1 text-text-tertiary hover:bg-surface-secondary hover:text-text-primary"
        >
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </button>
        <button
          type="button"
          aria-label={`Delete ${task.title}`}
          onClick={() => onDelete(task.id)}
          className="rounded p-1 text-text-tertiary hover:bg-error-light hover:text-error-dark"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      {/* Date and priority hold the right edge so they line up down the list */}
      <div className="flex w-[12.5rem] shrink-0 items-center justify-end gap-2">
        <DueDateField value={task.dueDate} onChange={(dueDate) => onPatch(task.id, { dueDate })} />
        <PriorityField value={task.priority} onChange={(priority) => onPatch(task.id, { priority })} />
      </div>
    </div>
  );
}
